import { RotateCw } from 'lucide-react';
import { MATERIAL_BY_NAME, shapeArea } from '@/lib/planMaterials';
import { getTexture, EDGES, inkFor, STRETCH } from '@/lib/planTextures';

const texStyle = (kind, color, scale) => {
  const t = getTexture(kind, color);
  if (t.stretch) return { backgroundImage: `url(${t.url})`, backgroundSize: '100% 100%', backgroundRepeat: 'no-repeat' };
  return { backgroundImage: `url(${t.url})`, backgroundSize: `${t.tileM * scale}px ${t.tileM * scale}px` };
};
const edgeKind = (edge) => (edge.id === 'brick' ? 'brick' : edge.id);

function SvgPattern({ id, kind, color, scale }) {
  const t = getTexture(kind, color); const sz = t.tileM * scale;
  return <pattern id={id} patternUnits="userSpaceOnUse" width={sz} height={sz}><image href={t.url} width={sz} height={sz} /></pattern>;
}

function Arrow({ x1, y1, x2, y2, ink }) {
  const m = ink === '#ffffff' ? 'url(#pd-arrow-light)' : 'url(#pd-arrow-dark)';
  return <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={ink} strokeWidth="0.9" markerStart={m} markerEnd={m} />;
}

// Architectural-style dimension lines inside the top and left edges
function Dims({ s, wpx, hpx, ink, fmtL, inset = 0 }) {
  if (wpx < 60 || hpx < 56) return null;
  const o = inset;
  const wl = fmtL(s.w), hl = fmtL(s.h);
  const tw = wl.length * 3.4 + 6, th = hl.length * 3.4 + 6;
  return (
    <svg width={wpx} height={hpx} className="absolute inset-0 pointer-events-none z-10" data-testid={`dims-${s.id}`}>
      <Arrow x1={6 + o} y1={11 + o} x2={wpx / 2 - tw} y2={11 + o} ink={ink} />
      <Arrow x1={wpx / 2 + tw} y1={11 + o} x2={wpx - 6 - o} y2={11 + o} ink={ink} />
      <text x={wpx / 2} y={11 + o} fill={ink} fontSize="10" fontWeight="500" textAnchor="middle" dominantBaseline="central" data-testid={`dim-w-${s.id}`}>{wl}</text>
      <Arrow x1={11 + o} y1={22 + o} x2={11 + o} y2={hpx / 2 - th} ink={ink} />
      <Arrow x1={11 + o} y1={hpx / 2 + th} x2={11 + o} y2={hpx - 6 - o} ink={ink} />
      <text x={11 + o} y={hpx / 2} fill={ink} fontSize="10" fontWeight="500" textAnchor="middle" dominantBaseline="central" transform={`rotate(-90 ${11 + o} ${hpx / 2})`} data-testid={`dim-h-${s.id}`}>{hl}</text>
    </svg>
  );
}

export function ArrowDefs() {
  return (
    <svg width="0" height="0" className="absolute">
      <defs>
        {[['dark', '#1f2937'], ['light', '#ffffff']].map(([id, c]) => (
          <marker key={id} id={`pd-arrow-${id}`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill={c} />
          </marker>
        ))}
      </defs>
    </svg>
  );
}

export function PlanShape2D({ s, scale, selected, drawing, realistic, showDims, fmtA, fmtL, onDown }) {
  const mat = MATERIAL_BY_NAME[s.name] || {};
  const isArc = (mat.wall || mat.canBow) && s.bow;
  const isPoly = s.type === 'poly';
  const wpx = s.w * scale, hpx = s.h * scale;
  const radius = s.type === 'circle' ? '50%' : (s.curve > 0 ? `${(s.curve * Math.min(s.w, s.h) / 2) * scale}px` : 0);
  const edge = EDGES.find((e) => e.id === s.edge && e.m);
  const fill = realistic ? texStyle(mat.tex, s.color, scale) : { background: s.color };
  const solid = !isArc && !isPoly;
  const ink = inkFor(mat.tex, s.color, realistic);
  const halo = ink === '#ffffff' ? '0 0 3px rgba(0,0,0,0.75)' : '0 0 3px rgba(255,255,255,0.85)';
  const thin = hpx < 16;
  const object = mat.tree || STRETCH.has(mat.tex);
  const dimsOn = showDims && solid && !mat.wall && !object && s.type !== 'circle' && wpx >= 60 && hpx >= 56;
  const raised = realistic && (s.z || 0) >= 0.3;
  const pid = `pd-${s.id}`;
  const svgFill = realistic ? `url(#${pid})` : s.color;
  const svgEdge = edge ? (realistic ? `url(#${pid}-e)` : '#64748b') : null;

  return (
    <div onPointerDown={(e) => onDown(e, s.id, 'move')} data-testid={`shape-${s.id}`}
      className={`absolute flex flex-col items-center justify-center cursor-move select-none ${selected ? 'outline outline-2 outline-offset-2 outline-brand' : ''}`}
      style={{
        left: s.x * scale, top: s.y * scale, width: wpx, height: hpx, borderRadius: radius, transform: `rotate(${s.rot || 0}deg)`,
        transformOrigin: 'center', overflow: 'visible', pointerEvents: drawing ? 'none' : 'auto', zIndex: Math.round((s.z || 0) * 10) + 1,
        filter: raised ? 'drop-shadow(2px 3px 2.5px rgba(0,0,0,0.38))' : undefined,
        ...(solid ? (edge ? (realistic ? texStyle(edgeKind(edge), edge.color, scale) : { background: '#64748b' }) : fill) : {}),
        opacity: realistic ? 1 : 0.9,
      }}>
      {solid && edge && (
        <div className="absolute pointer-events-none" style={{ inset: edge.m * scale, borderRadius: s.type === 'circle' ? '50%' : (s.curve > 0 ? `calc(${radius} - ${edge.m * scale}px)` : 0), ...fill }} />
      )}
      {(isArc || isPoly) && (
        <svg width={wpx} height={hpx} style={{ position: 'absolute', left: 0, top: 0, overflow: 'visible', pointerEvents: 'none' }}>
          {realistic && <defs><SvgPattern id={pid} kind={mat.tex} color={s.color} scale={scale} />{edge && <SvgPattern id={`${pid}-e`} kind={edgeKind(edge)} color={edge.color} scale={scale} />}</defs>}
          {isArc && <path d={`M0,${hpx / 2} Q${wpx / 2},${hpx / 2 - s.bow * wpx / 2} ${wpx},${hpx / 2}`} stroke={svgFill} strokeWidth={Math.max(3, hpx)} fill="none" strokeLinecap="round" />}
          {isPoly && s.points?.length >= 3 && (
            <polygon points={s.points.map((p) => `${p.x * scale},${p.y * scale}`).join(' ')} fill={svgFill}
              stroke={svgEdge || (realistic ? 'rgba(0,0,0,0.25)' : '#0f172a')} strokeWidth={edge ? edge.m * scale : 1} strokeLinejoin="round" />
          )}
        </svg>
      )}
      {realistic && mat.roof && (
        <svg width={wpx} height={hpx} className="absolute inset-0 pointer-events-none" data-testid={`roof-${s.id}`}>
          <path d={wpx >= hpx
            ? `M0,0 L${hpx / 2},${hpx / 2} L${wpx - hpx / 2},${hpx / 2} L${wpx},0 M0,${hpx} L${hpx / 2},${hpx / 2} M${wpx - hpx / 2},${hpx / 2} L${wpx},${hpx}`
            : `M0,0 L${wpx / 2},${wpx / 2} L${wpx / 2},${hpx - wpx / 2} L0,${hpx} M${wpx},0 L${wpx / 2},${wpx / 2} M${wpx / 2},${hpx - wpx / 2} L${wpx},${hpx}`}
            stroke="rgba(20,22,26,0.7)" strokeWidth="2.5" fill="none" />
        </svg>
      )}
      {dimsOn && <Dims s={s} wpx={wpx} hpx={hpx} ink={ink} fmtL={fmtL} inset={edge ? edge.m * scale : 0} />}
      <span className={`z-10 text-center leading-tight text-[10px] font-semibold uppercase tracking-wider ${thin ? 'absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap' : object ? 'absolute top-full mt-0.5 left-1/2 -translate-x-1/2 whitespace-nowrap' : 'relative px-1'}`}
        style={{ color: thin || object ? '#1f2937' : ink, textShadow: thin || object ? '0 0 3px rgba(255,255,255,0.95)' : halo, maxWidth: thin || object ? undefined : Math.max(40, wpx - (dimsOn ? 34 : 6)) }}>
        {mat.wall ? `${s.name} · ${fmtL(Math.max(s.w, s.h))}` : s.name}
        {s.type === 'circle' && showDims && <span className="block text-[9px] font-medium normal-case">Ø {fmtL(s.w)}</span>}
      </span>
      {!mat.wall && <span data-testid={`area-${s.id}`} className={`${object ? 'sr-only' : 'relative'} z-10 text-[9px] font-medium opacity-90`} style={{ color: ink, textShadow: halo }}>{fmtA(shapeArea(s))}</span>}
      {selected && (
        <>
          {!dimsOn && (
            <>
              <span className="absolute top-0.5 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-1 rounded-sm text-[9px] leading-none py-0.5 pointer-events-none whitespace-nowrap z-20" data-testid={`dim-w-${s.id}`}>{fmtL(s.w)}</span>
              <span className="absolute left-0.5 top-1/2 -translate-y-1/2 bg-slate-900 text-white px-1 rounded-sm text-[9px] leading-none py-0.5 pointer-events-none whitespace-nowrap z-20" data-testid={`dim-h-${s.id}`}>{fmtL(s.h)}</span>
            </>
          )}
          <span onPointerDown={(e) => onDown(e, s.id, 'resize')} className="absolute -right-1.5 -bottom-1.5 w-4 h-4 rounded-sm bg-brand border border-white cursor-se-resize z-20" data-testid={`resize-${s.id}`} />
          <span onPointerDown={(e) => onDown(e, s.id, 'rotate')} title="Drag to rotate" className="absolute left-1/2 -top-7 -translate-x-1/2 w-5 h-5 rounded-full bg-slate-900 border border-white cursor-grab flex items-center justify-center z-20" data-testid={`rotate-${s.id}`}><RotateCw size={10} className="text-white" /></span>
        </>
      )}
    </div>
  );
}

// North arrow + scale bar pinned to the board corner
export function ScaleBadge({ scale, light }) {
  const len = [1, 2, 5, 10].find((m) => m * scale >= 60) || 10;
  const L = len * scale, ink = light ? '#ffffff' : '#1f2937', alt = light ? 'rgba(0,0,0,0.55)' : '#ffffff';
  return (
    <svg width={L + 46} height={44} className="absolute right-2 bottom-2 pointer-events-none z-[60]" data-testid="plan-scale-badge"
      style={{ filter: light ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))' : undefined }}>
      <path d="M12,6 L18,30 L12,25 Z" fill={ink} /><path d="M12,6 L6,30 L12,25 Z" fill="none" stroke={ink} />
      <text x="12" y="42" fill={ink} fontSize="10" fontWeight="700" textAnchor="middle">N</text>
      {[0, 1, 2, 3].map((i) => <rect key={i} x={36 + (i * L) / 4} y={30} width={L / 4} height={5} fill={i % 2 ? alt : ink} />)}
      <rect x={36} y={30} width={L} height={5} fill="none" stroke={ink} strokeWidth="0.8" />
      <text x={36} y={25} fill={ink} fontSize="11" fontWeight="700" textAnchor="middle">0</text>
      <text x={36 + L} y={25} fill={ink} fontSize="11" fontWeight="700" textAnchor="end">{len} m</text>
    </svg>
  );
}
